<template>
  <div v-show="!isMobile" class="live2d-widget-container" @click="handleClick">
    <!-- 对话框 -->
    <div v-if="showDialog" class="live2d-dialog">
      {{ currentMessage }}
    </div>
  </div>
</template>

<script>
import { LIVE2D_CONFIG } from './Live2DConfig'
import { LIVE2D_MODELS, getTimeSlotModel } from './Live2DModels'
import { getHitokoto } from '@/api/live2d'

export default {
  name: 'Live2DWidget',
  
  props: {
    enable: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'right-bottom'
    }
  },
  
  data() {
    return {
      config: LIVE2D_CONFIG,
      isMobile: false,
      showDialog: false,
      currentMessage: '',
      cachedModel: null,
      live2dInstance: null,
      observer: null,
      unifiedTimer: null,
      dialogTimer: null,
      resizeTimer: null,
      lastHitokotoTime: 0,
      lastIdleTime: 0,
      lastTimeSlotCheck: 0,
      destroyed: false
    }
  },
  
  computed: {
    currentModel() {
      return this.cachedModel || (this.cachedModel = getTimeSlotModel())
    }
  },
  
  mounted() {
    if (!this.enable) {
      return
    }
    
    this.checkMobile()
    this.loadLive2D()
    
    window.addEventListener('resize', this.handleResize)
    
    this.observeLive2DElement()
    
    this.startUnifiedTimer()
  },
  
  beforeDestroy() {
    this.destroyed = true

    if (this.observer) {
      this.observer.disconnect()
    }
    
    window.removeEventListener('resize', this.handleResize)
    
    if (this.unifiedTimer) {
      clearInterval(this.unifiedTimer)
    }
    
    if (this.dialogTimer) {
      clearTimeout(this.dialogTimer)
    }
    
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    
    if (this.live2dInstance) {
      try {
        this.live2dInstance.destroy()
      } catch (error) {
        console.error('销毁 Live2D 实例失败:', error)
      }
    }

    // 官方组件会把画布注入到 body，销毁实例后仍需移除残留节点
    this.removeInjectedElements()
  },

  methods: {
    /**
     * 移除 Live2D 官方组件注入到 body 的残留节点
     */
    removeInjectedElements() {
      const ids = ['live2d-widget', 'live2dcanvas']
      ids.forEach(id => {
        const element = document.getElementById(id)
        if (element && element.parentNode) {
          element.parentNode.removeChild(element)
        }
      })
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768
    },
    
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.checkMobile()
      }, 200)
    },
    
    observeLive2DElement() {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.id === 'live2dcanvas') {
                this.moveLive2DElement(node)
              }
              
              const canvasElement = node.querySelector('#live2dcanvas')
              if (canvasElement) {
                this.moveLive2DElement(canvasElement)
              }
            }
          })
        })
      })
      
      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      })
    },
    
    moveLive2DElement(canvasElement) {
      if (canvasElement && this.$el) {
        const isInContainer = canvasElement.closest('.live2d-widget-container') === this.$el
        
        if (isInContainer) {
          return
        }
        
        let live2dContainer = canvasElement.parentElement
        
        while (live2dContainer && !live2dContainer.classList.contains('live2d-widget') && live2dContainer.id !== 'live2d-widget') {
          live2dContainer = live2dContainer.parentElement
        }
        
        if (live2dContainer) {
          live2dContainer.style.position = 'static'
          live2dContainer.style.right = 'auto'
          live2dContainer.style.bottom = 'auto'
          live2dContainer.style.zIndex = '9999'
          
          this.$el.appendChild(live2dContainer)
          
          if (this.observer) {
            this.observer.disconnect()
          }
        }
      }
    },
    
    async loadLive2D() {
      try {
        await this.loadLive2DLibrary()
        // 组件已销毁（例如进入 FX 页面）则不再初始化
        if (this.destroyed) {
          return
        }
        await this.initLive2DInstance()
        // 模型加载完成后可能再次注入节点，此时需清理
        if (this.destroyed) {
          this.removeInjectedElements()
          return
        }

        this.showWelcomeDialog()
        
        if (this.config.dialog.useHitokoto) {
          this.fetchHitokoto()
        }
      } catch (error) {
        console.error('Live2D 加载失败:', error)
      }
    },
    
    loadLive2DLibrary() {
      return new Promise((resolve, reject) => {
        if (window.L2Dwidget) {
          resolve()
          return
        }
        
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/live2d-widget@latest/lib/L2Dwidget.min.js'
        script.onload = resolve
        script.onerror = reject
        document.body.appendChild(script)
      })
    },
    
    async initLive2DInstance() {
      const model = this.currentModel
      
      return new Promise((resolve, reject) => {
        try {
          this.live2dInstance = window.L2Dwidget.init({
            model: {
              jsonPath: model.model
            },
            display: {
              position: 'right',
              width: this.config.widget.width,
              height: this.config.widget.height,
              hOffset: 0,
              vOffset: 0
            },
            mobile: {
              show: false
            },
            react: {
              opacityDefault: 1,
              opacityOnHover: 1
            }
          })
          
          if (this.destroyed) {
            try {
              this.live2dInstance.destroy()
            } catch (error) {
              console.error('销毁 Live2D 实例失败:', error)
            }
            this.removeInjectedElements()
          }

          setTimeout(() => {
            if (this.destroyed) {
              this.removeInjectedElements()
            }
            resolve()
          }, 1000)
        } catch (error) {
          reject(error)
        }
      })
    },
    
    async fetchHitokoto() {
      try {
        const res = await getHitokoto()
        if (res.data && res.data.hitokoto) {
          const hitokoto = res.data.hitokoto
          const message = `${hitokoto.hitokoto} —— ${hitokoto.from}`
          this.showDialogMessage('hitokoto', message)
        }
      } catch (error) {
        console.error('获取一言语录失败:', error)
      }
    },
    
    startUnifiedTimer() {
      const now = Date.now()
      this.lastHitokotoTime = now - this.config.dialog.hitokotoInterval
      this.lastIdleTime = now - 300000
      this.lastTimeSlotCheck = now - 600000
      
      this.unifiedTimer = setInterval(() => {
        const currentTime = Date.now()
        
        const hitokotoInterval = this.config.dialog.hitokotoInterval
        const idleInterval = 300000
        const timeSlotCheckInterval = 600000
        
        if (currentTime - this.lastHitokotoTime >= hitokotoInterval) {
          this.fetchHitokoto()
          this.lastHitokotoTime = currentTime
        }
        
        if (currentTime - this.lastIdleTime >= idleInterval) {
          if (!this.showDialog) {
            this.showRandomDialog('idle')
          }
          this.lastIdleTime = currentTime
        }
        
        if (currentTime - this.lastTimeSlotCheck >= timeSlotCheckInterval) {
          this.checkTimeSlot()
          this.lastTimeSlotCheck = currentTime
        }
      }, 60000)
    },
    
    checkTimeSlot() {
      const newModel = getTimeSlotModel()
      const currentModel = this.currentModel
      
      if (newModel && currentModel && newModel.id !== currentModel.id) {
        this.switchModelByTimeSlot(newModel)
      }
    },
    
    async switchModelByTimeSlot(newModel) {
      // 先将 Live2D 元素放回 document.body 中
      const live2dElement = document.getElementById('live2d-widget')
      if (live2dElement) {
        document.body.appendChild(live2dElement)
      }
      
      // 销毁现有的 Live2D 实例
      if (this.live2dInstance) {
        try {
          this.live2dInstance.destroy()
        } catch (error) {
          console.error('销毁 Live2D 实例失败:', error)
        }
        this.live2dInstance = null
      }
      
      // 初始化新的 Live2D 实例
      await this.initLive2DInstance()
      
      // 重新开始观察 Live2D 元素的创建
      this.observeLive2DElement()
    },
    
    showWelcomeDialog() {
      if (!this.config.dialog.enable) return
      
      const messages = this.config.dialog.welcome
      const message = messages[Math.floor(Math.random() * messages.length)]
      this.showDialogMessage('custom', message)
    },
    
    handleClick() {
      this.showRandomDialog('click')
    },
    
    showRandomDialog(type) {
      if (!this.config.dialog.enable) return
      
      const messages = this.config.dialog[type]
      const message = messages[Math.floor(Math.random() * messages.length)]
      this.showDialogMessage('custom', message)
    },
    
    showDialogMessage(type, customMessage) {
      if (!this.config.dialog.enable) return
      
      let message = customMessage
      
      if (!message && type !== 'hitokoto') {
        const messages = this.config.dialog[type]
        message = messages[Math.floor(Math.random() * messages.length)]
      }
      
      this.currentMessage = message
      this.showDialog = true
      
      if (this.dialogTimer) {
        clearTimeout(this.dialogTimer)
      }
      
      this.dialogTimer = setTimeout(() => {
        this.showDialog = false
      }, 5000)
    }
  }
}
</script>

<style scoped>
.live2d-widget-container {
  position: fixed;
  bottom: 20px;
  right: 5% !important;
  z-index: 9999;
  cursor: pointer;
  width: 280px;
  height: 350px;
}

.live2d-dialog {
  position: absolute;
  bottom: 100%;
  right: 0;
  background: white;
  padding: 10px 15px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  margin-bottom: 10px;
  animation: fadeIn 0.3s ease;
  max-width: 300px;
  word-wrap: break-word;
  z-index: 10000;
}

.live2d-dialog::after {
  content: '';
  position: absolute;
  bottom: -6px;
  right: 20px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .live2d-widget-container {
    display: none;
  }
}

/* 确保 Live2D 官方组件的样式不会冲突 */
:global(.live2d-widget) {
  z-index: 9999 !important;
  position: static !important;
}

:global(.live2d-widget .live2d-widget-dialog) {
  display: none !important;
}
</style>